import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Icon,
  Image,
  Button,
  CircularProgress,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { HiLocationMarker } from "react-icons/hi";
import { IoPricetag } from "react-icons/io5";
import { IoIosArrowForward } from "react-icons/io";
import { FaMessage } from "react-icons/fa6";
import { FaStar } from "react-icons/fa";

import { Services } from "../features/bookingLink/Services";
import { Info } from "../features/bookingLink/Info";
import { Portfolio } from "../features/bookingLink/Portfolio";
import { Reviews } from "../features/bookingLink/Reviews";

import { getProviderDetailsBySlug, getProfile } from "../services/profile";
import { BUSINESS_IMAGE_URL, PROFILE_IMAGE_URL } from "../core/constants";
import { getCookie, formatDurationHM } from "../core/utils";

import LogoIcon from "../assets/images/logoIcon.png";
import LogoText from "../assets/images/logoText.png";
import MastHeadLogo from "../assets/images/mastHeadLogo.png";
import ProfileDefault from "../assets/images/profileDefault.png";

export const BookingLink = () => {
  const { slug } = useParams();

  const navigate = useNavigate();

  const drawerDisclosure = useDisclosure();

  const [activeTab, setActiveTab] = useState<string>("Services");
  const [providerDetails, setProviderDetails] = useState<any>();
  const [profile, setProfile] = useState<any>();

  const [cart, setCart] = useState<any[]>([]);

  useEffect(() => {
    const fetchProviderDetails = async () => {
      if (slug) {
        const data = await getProviderDetailsBySlug(slug);
        if (!data || Object.keys(data).length === 0) {
          navigate("/booking-link-not-found", { replace: true });
          return;
        }
        setProviderDetails(data);
      }
    };

    fetchProviderDetails();
  }, [slug, navigate]);

  const jwt = getCookie("book-d booking token");

  useEffect(() => {
    const fetchProfile = async () => {
      if (jwt && !profile) {
        setProfile(await getProfile());
      }
    };

    fetchProfile();
  }, [jwt, profile]);

  const active = {
    py: "9px",
    px: "12px",
    borderRadius: "30px",
    bg: "#FF8E3C",
  };

  const nav = [
    {
      id: 1,
      name: "Services",
      component: (
        <Services
          id={providerDetails?.business_account_id}
          cart={cart}
          setCart={setCart}
          drawerDisclosure={drawerDisclosure}
          slug={slug}
          name={profile?.full_name}
        />
      ),
    },
    {
      id: 2,
      name: "Info",
      component: (
        <Info
          id={providerDetails?.business_account_id}
          providerDetails={providerDetails}
        />
      ),
    },
    {
      id: 3,
      name: "Portfolio",
      component: <Portfolio id={providerDetails?.business_account_id} />,
    },
    {
      id: 4,
      name: "Reviews",
      component: <Reviews id={providerDetails?.business_account_id} />,
    },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id.toLocaleLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    setActiveTab(id);
  };

  return (
    <Box
      display={"flex"}
      flexDir={"column"}
      height={"100vh"}
      overflow={"hidden"}
    >
      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        minH="72px"
        px={{ base: "20px", md: "10%" }}
        align={"center"}
        justify={"space-between"}
      >
        <HStack gap={"40px"}>
          <Image
            src={LogoIcon}
            alt="Book-d Logo"
            h={"32px"}
            w="19px"
            onClick={() => navigate("/")}
          />

          <Flex gap={"10px"} display={{ base: "none", md: "flex" }}>
            {nav.map((menuItem, index) => (
              <Box
                cursor={"pointer"}
                key={index}
                onClick={() => scrollToSection(menuItem.name)}
              >
                <Box px="10px" pb="6px" key={menuItem.id}>
                  <Text
                    fontSize={"16px"}
                    color={activeTab === menuItem.name ? "#3F3434" : "#9BA3A3"}
                    fontWeight={600}
                  >
                    {menuItem.name}
                  </Text>
                </Box>

                {activeTab === menuItem.name && (
                  <Box h={"3px"} borderRadius={"10px"} bg={"#3F3434"} />
                )}
              </Box>
            ))}
          </Flex>
        </HStack>

        {jwt ? (
          <HStack gap={"12px"}>
            <Image
              src={`${PROFILE_IMAGE_URL}/${profile?.profile_image}`}
              fallbackSrc={ProfileDefault}
              alt="User image"
              h={"40px"}
              w="40px"
              borderRadius={"full"}
              objectFit={"cover"}
            />

            <Box>
              <Heading fontSize={"14px"}>{profile?.full_name}</Heading>
              <Text fontSize={"14px"}>{profile?.email}</Text>
            </Box>
          </HStack>
        ) : (
          <HStack>
            <Button
              bg={"#FF8E3C"}
              _hover={{ bg: "#FF8E3C" }}
              borderRadius={"12px"}
              color={"white"}
              h="48px"
              fontSize={"15px"}
              lineHeight={"18.2px"}
              onClick={() =>
                navigate("/login", {
                  state: { returnSlug: slug },
                })
              }
              fontFamily={"RedHatDisplay-Bold"}
            >
              Login
            </Button>

            <Button
              bg={"#FFFFFF"}
              _hover={{ bg: "#FF8E3C", color: "white" }}
              borderRadius={"12px"}
              color={"#FF8E3C"}
              h="48px"
              fontSize={"15px"}
              lineHeight={"18.2px"}
              onClick={() =>
                navigate("/signup", {
                  state: { returnSlug: slug },
                })
              }
              fontFamily={"RedHatDisplay-Bold"}
            >
              Signup
            </Button>
          </HStack>
        )}
      </Flex>

      <Box h={`calc(100vh - 72px)`} overflowY={"scroll"} pt="40px">
        {providerDetails ? (
          <Flex
            flexDir={"row"}
            gap={"56px"}
            align={"flex-start"}
            px={{ base: "20px", md: "10%" }}
          >
            <Box w={{ base: "100%", md: "60%" }} flex={1}>
              <Flex
                gap={"6px"}
                align={"center"}
                mb="32px"
                display={{ base: "none", md: "flex" }}
              >
                <Text fontSize={"16px"} color={"#4F4F51"} lineHeight={"24px"}>
                  Home
                </Text>

                <Icon fontSize={"20px"} color={"#19202D"}>
                  <IoIosArrowForward />
                </Icon>

                <Text fontSize={"16px"} color={"#FF762C"} lineHeight={"24px"}>
                  {providerDetails.businessName}
                </Text>
              </Flex>

              <Flex
                flexDir={{ base: "column", md: "row" }}
                align={{ base: "center", md: "flex-start" }}
                justify={{ base: "center", md: "flex-start" }}
                gap={{ base: 0, md: "20px" }}
              >
                <Image
                  src={`${BUSINESS_IMAGE_URL}/${providerDetails.businessLogo}`}
                  fallbackSrc={ProfileDefault}
                  alt="User image"
                  h={"96px"}
                  w="96px"
                  borderRadius={"full"}
                  objectFit={"cover"}
                />

                <Flex
                  mt={{ base: "20px", md: 0 }}
                  flexDir={"column"}
                  gap={"4px"}
                >
                  <Heading
                    fontSize={"22px"}
                    color={"#19202D"}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    {providerDetails.businessName}
                  </Heading>

                  <Flex
                    gap={"4px"}
                    justify={{ base: "center", md: "flex-start" }}
                  >
                    {providerDetails.serviceTypes?.map(
                      (serviceType: string, index: number) => (
                        <Box
                          bg="#DBF1F2"
                          borderRadius={"4px"}
                          px="8px"
                          py="3px"
                          key={index}
                        >
                          <Text
                            color={"#267684"}
                            fontSize={"14px"}
                            fontWeight={400}
                            textTransform={"capitalize"}
                          >
                            {serviceType}
                          </Text>
                        </Box>
                      ),
                    )}
                  </Flex>

                  <Flex
                    gap={"4px"}
                    justify={{ base: "center", md: "flex-start" }}
                  >
                    <Icon fontSize={"24px"} color={"#9BA3A3"}>
                      <HiLocationMarker />
                    </Icon>

                    <Text fontSize={"16px"} color={"#4F4F51"}>
                      {providerDetails.location}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>

              <Flex
                mt="20px"
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                py="12px"
                px="32px"
                borderRadius={"20px"}
                w="100%"
                align={"center"}
                justify={"space-between"}
                display={{ base: "flex", md: "none" }}
              >
                <Flex
                  flexDir={"column"}
                  align={"center"}
                  gap={"4px"}
                  onClick={() => navigate("/")}
                >
                  <Icon fontSize={"24px"} color={"#FF8E3C"}>
                    <FaMessage />
                  </Icon>

                  <Text fontSize={"14px"} color={"#19202D"}>
                    Message
                  </Text>
                </Flex>

                <Flex flexDir={"column"} align={"center"} gap={"4px"}>
                  <Icon fontSize={"24px"} color={"#8FD3D6"}>
                    <IoPricetag />
                  </Icon>

                  <Text fontSize={"14px"} color={"#19202D"}>
                    £ {providerDetails.min_price}-{providerDetails.max_price}
                  </Text>
                </Flex>

                <Flex flexDir={"column"} align={"center"} gap={"4px"}>
                  <Icon fontSize={"24px"} color={"#FFC266"}>
                    <FaStar />
                  </Icon>

                  <Text fontSize={"14px"} color={"#19202D"}>
                    {providerDetails.avg_rating?.toFixed(1)} (
                    {providerDetails.reviews_count})
                  </Text>
                </Flex>
              </Flex>

              <Flex
                mt="20px"
                boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                py="6px"
                px="8px"
                borderRadius={"40px"}
                w="100%"
                align={"center"}
                justify={"space-between"}
                display={{ base: "flex", md: "none" }}
              >
                {nav.map((menuItem) => (
                  <Box
                    key={menuItem.id}
                    onClick={() => setActiveTab(menuItem.name)}
                    sx={
                      activeTab === menuItem.name
                        ? active
                        : menuItem.id === 1
                          ? { pl: "12px" }
                          : menuItem.id === 4
                            ? { pr: "12px" }
                            : {}
                    }
                  >
                    {activeTab === menuItem.name ? (
                      <Heading
                        fontSize={"16px"}
                        color="#FFF"
                        lineHeight={"24px"}
                        fontFamily={"RedHatDisplay-Bold"}
                      >
                        {menuItem.name}
                      </Heading>
                    ) : (
                      <Text
                        fontSize={"16px"}
                        color={"#4F4F51"}
                        lineHeight={"24px"}
                      >
                        {menuItem.name}
                      </Text>
                    )}
                  </Box>
                ))}
              </Flex>

              <Box display={{ base: "block", md: "none" }}>
                {nav.find((menuItem) => menuItem.name === activeTab)?.component}
              </Box>

              <Box display={{ base: "none", md: "block" }}>
                <Box id="services">
                  <Services
                    id={providerDetails?.business_account_id}
                    cart={cart}
                    setCart={setCart}
                    drawerDisclosure={drawerDisclosure}
                    slug={slug}
                    name={profile?.full_name}
                  />
                </Box>

                <Box mt="30px" id="info">
                  <Info
                    id={providerDetails?.business_account_id}
                    providerDetails={providerDetails}
                  />
                </Box>

                <Box
                  h={"1px"}
                  w={"100%"}
                  borderRadius={"10px"}
                  bg="#E9EDEF"
                  my="20px"
                />

                <Box mt="30px" mb="40px" id="portfolio">
                  <Flex justify={"space-between"} align={"center"}>
                    <Heading
                      fontSize={{ base: "18px", md: "24px" }}
                      color={"#19202D"}
                    >
                      Portfolio
                    </Heading>
                  </Flex>

                  <Portfolio id={providerDetails?.business_account_id} />
                </Box>

                <Box
                  h={"1px"}
                  w={"100%"}
                  borderRadius={"10px"}
                  bg="#E9EDEF"
                  my="20px"
                  id="reviews"
                />

                <Box mt="30px">
                  <Heading
                    fontSize={{ base: "18px", md: "24px" }}
                    color={"#19202D"}
                  >
                    Reviews
                  </Heading>

                  <Reviews id={providerDetails?.business_account_id} />
                </Box>
              </Box>
            </Box>

            <Box
              w={"40%"}
              display={{ base: "none", md: "block" }}
              bg={
                "linear-gradient(#FFFFFF, #FFFFFF, #FFFFFF, #FFF7F3, #D8F1F2)"
              }
              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.2)"
              p={"20px"}
              borderRadius={"20px"}
              pos={"sticky"}
              top={0}
              h={"fit-content"}
            >
              <Heading fontSize={"22px"} color={"#19202D"}>
                Selected
              </Heading>

              <Box
                h={"1px"}
                w={"100%"}
                borderRadius={"10px"}
                bg="#E9EDEF"
                my="20px"
              />

              {cart.length === 0 ? (
                <Text fontSize={"14px"} color={"#4F4F51"} textAlign={"center"}>
                  You haven't added any services yet
                </Text>
              ) : (
                <Box>
                  {cart.map((item, index) => {
                    // The duration the selected length/size actually resolved
                    // to, already computed when the item was added to the cart.
                    const totalDurationMin = Number(
                      item.finalDuration ?? item.service.duration_minute ?? 0,
                    );

                    return (
                      <Box mb="20px" key={index}>
                        <Heading
                          fontSize={"16px"}
                          color="#4F4F51"
                          fontFamily={"RedHatDisplay-Bold"}
                        >
                          {item.service.name}
                        </Heading>

                        <Text fontSize={"14px"} color="#4F4F51" my="8px">
                          {item.service.description}
                        </Text>

                        <Flex gap={"10px"}>
                          <Flex align={"center"} gap={"4px"}>
                            <Icon viewBox="0 0 16 16">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_700_1180)">
                                  <path
                                    d="M12.6666 9.33342V4.00008C12.6666 3.26675 12.0666 2.66675 11.3333 2.66675H1.99996C1.26663 2.66675 0.666626 3.26675 0.666626 4.00008V9.33342C0.666626 10.0667 1.26663 10.6667 1.99996 10.6667H11.3333C12.0666 10.6667 12.6666 10.0667 12.6666 9.33342ZM6.66663 8.66675C5.55996 8.66675 4.66663 7.77341 4.66663 6.66675C4.66663 5.56008 5.55996 4.66675 6.66663 4.66675C7.77329 4.66675 8.66663 5.56008 8.66663 6.66675C8.66663 7.77341 7.77329 8.66675 6.66663 8.66675ZM15.3333 4.66675V12.0001C15.3333 12.7334 14.7333 13.3334 14 13.3334H2.66663V12.0001H14V4.66675H15.3333Z"
                                    fill="#FF8E3C"
                                    fillOpacity="0.5"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_700_1180">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </Icon>

                            <Text
                              fontSize={"16px"}
                              fontWeight={600}
                              color={"#267684"}
                            >
                              £ {item.finalPrice}
                            </Text>
                          </Flex>

                          <Flex align={"center"} gap={"4px"}>
                            <Icon viewBox="0 0 16 16">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_700_1189)">
                                  <path
                                    d="M7.99337 1.33325C4.31337 1.33325 1.33337 4.31992 1.33337 7.99992C1.33337 11.6799 4.31337 14.6666 7.99337 14.6666C11.68 14.6666 14.6667 11.6799 14.6667 7.99992C14.6667 4.31992 11.68 1.33325 7.99337 1.33325ZM8.00004 13.3333C5.05337 13.3333 2.66671 10.9466 2.66671 7.99992C2.66671 5.05325 5.05337 2.66659 8.00004 2.66659C10.9467 2.66659 13.3334 5.05325 13.3334 7.99992C13.3334 10.9466 10.9467 13.3333 8.00004 13.3333Z"
                                    fill="#FF8E3C"
                                    fillOpacity="0.5"
                                  />
                                  <path
                                    d="M8.33337 4.66675H7.33337V8.66675L10.8334 10.7667L11.3334 9.94675L8.33337 8.16675V4.66675Z"
                                    fill="#FF8E3C"
                                    fillOpacity="0.5"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_700_1189">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </Icon>

                            <Text
                              fontSize={"16px"}
                              fontWeight={600}
                              color={"#267684"}
                            >
                              {formatDurationHM(totalDurationMin)}
                            </Text>
                          </Flex>
                        </Flex>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Button
                bg={"#FF8E3C"}
                _hover={{ bg: "#FF8E3C" }}
                my="20px"
                borderRadius={"30px"}
                color={"white"}
                h="58px"
                fontSize={"17px"}
                fontFamily={"RedHatDisplay-Bold"}
                onClick={() => drawerDisclosure.onOpen()}
                w={"100%"}
                disabled={cart.length === 0}
                rightIcon={
                  cart.length !== 0 ? (
                    <Flex
                      w={"30px"}
                      h={"30px"}
                      borderRadius={"full"}
                      bg="black"
                      align={"center"}
                      justify={"center"}
                    >
                      <Text fontWeight={700} color={"white"}>
                        {cart.length}
                      </Text>
                    </Flex>
                  ) : (
                    <Box />
                  )
                }
              >
                Checkout
              </Button>

              <Text
                fontSize={"14px"}
                color={"#4F4F51"}
                textAlign={"center"}
                mt="10px"
              >
                Scheduling made simple by
              </Text>

              <Image
                src={LogoText}
                h="30px"
                objectFit={"contain"}
                justifySelf="center"
                my="10px"
              />
            </Box>
          </Flex>
        ) : (
          <Flex mt="40px" flex={1}>
            <CircularProgress
              size={"70px"}
              flex={1}
              justifyItems={"center"}
              isIndeterminate
              color="#FF8E3C"
            />
          </Flex>
        )}

        <Box
          mt="80px"
          bg={"linear-gradient(#FFFFFF, #FFF7F3, #D8F1F2)"}
          h={"135px"}
          w={"100vw"}
          p="20px"
        >
          <Text fontSize={"14px"} textAlign={"center"} color="#4F4F51">
            Scheduling made simple by{" "}
          </Text>

          <Image
            src={MastHeadLogo}
            objectFit={"contain"}
            h={"30px"}
            mt="10px"
            justifySelf={"center"}
          />
        </Box>
      </Box>
    </Box>
  );
};
